import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  DirectionsServiceEnum,
  editFormDataAF,
  formatRebuildRouteAppliedInfo,
  formatRebuildRouteTypeLabel,
  IRideDto,
  isCustomRebuildRouteType,
  lastRouteRebuildAtAtom,
  lastSelectedRebuildTypeAtom,
  RebuildRouteTypeEnum,
  resolveDirectionsServiceLabel,
  resolveInitialRebuildRouteProvider,
  RideCrudModeKeyTuple,
  ROUTE_REBUILD_SOURCE_TRANSLATION_KEY_MAP,
  RouteRebuildSourceEnum,
  useFormats,
  useSystemParams,
} from "@ryde/shared";

const formatRebuildRouteSourceLabel = (
  routeRebuildSource: RouteRebuildSourceEnum | null | undefined,
  t: (key: string) => string,
): string | undefined => {
  if (!routeRebuildSource) {
    return undefined;
  }

  return t(ROUTE_REBUILD_SOURCE_TRANSLATION_KEY_MAP[routeRebuildSource]);
};

const formatRebuildRouteAppliedDetail = ({
  rebuildRouteType,
  provider,
  providerLabel,
  appliedAtLabel,
  routeRebuildSource,
  t,
}: {
  rebuildRouteType: RebuildRouteTypeEnum;
  provider: DirectionsServiceEnum;
  providerLabel: string;
  appliedAtLabel?: string;
  routeRebuildSource?: RouteRebuildSourceEnum | null;
  t: (key: string) => string;
}): string => {
  const actionLabel = formatRebuildRouteTypeLabel({
    provider,
    rebuildType: rebuildRouteType,
    t,
  });
  const sourceLabel = formatRebuildRouteSourceLabel(routeRebuildSource, t);
  const detailParts = [`${actionLabel} (${providerLabel})`];

  if (sourceLabel) {
    detailParts.push(sourceLabel);
  }

  if (appliedAtLabel) {
    detailParts.push(appliedAtLabel);
  }

  return detailParts.join(" · ");
};

export const useRideRebuildRouteAppliedInfo = () => {
  const { t } = useTranslation();
  const { formatDateTimeWithSecondsNormalized } = useFormats();
  const systemParams = useSystemParams();
  const rideDto = useRecoilValue<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const lastSelectedRebuildType = useRecoilValue(lastSelectedRebuildTypeAtom);
  const lastRouteRebuildAt = useRecoilValue(lastRouteRebuildAtAtom);

  const resolveAppliedInfo = useMemo(() => {
    const systemDefaultProvider =
      systemParams.directionsService || DirectionsServiceEnum.Google;

    return ({
      rebuildRouteType,
      routeRebuildAt,
      routeRebuildSource,
    }: {
      rebuildRouteType: RebuildRouteTypeEnum | null | undefined;
      routeRebuildAt?: TDateISO | null;
      routeRebuildSource?: RouteRebuildSourceEnum | null;
    }) => {
      if (!rebuildRouteType || !isCustomRebuildRouteType(rebuildRouteType)) {
        return undefined;
      }

      const provider = resolveInitialRebuildRouteProvider({
        lastUsedProvider: rideDto?.metadata?.directionsService,
        systemDefaultProvider,
      });
      const providerLabel = resolveDirectionsServiceLabel(provider) ?? provider;
      const appliedAtLabel = routeRebuildAt
        ? formatDateTimeWithSecondsNormalized(routeRebuildAt)
        : undefined;

      return {
        bannerText: formatRebuildRouteAppliedInfo({
          providerLabel,
          provider,
          appliedAtLabel,
          rebuildRouteType,
          routeRebuildSource,
          t,
        }),
        detailText: formatRebuildRouteAppliedDetail({
          rebuildRouteType,
          provider,
          providerLabel,
          appliedAtLabel,
          routeRebuildSource,
          t,
        }),
      };
    };
  }, [
    formatDateTimeWithSecondsNormalized,
    rideDto?.metadata?.directionsService,
    systemParams.directionsService,
    t,
  ]);

  const sessionRebuildBannerText = useMemo(() => {
    if (!lastSelectedRebuildType) {
      return undefined;
    }

    return resolveAppliedInfo({
      rebuildRouteType: lastSelectedRebuildType,
      routeRebuildAt: lastRouteRebuildAt ?? rideDto?.metadata?.routeRebuildAt,
      routeRebuildSource: rideDto?.metadata?.routeRebuildSource,
    })?.bannerText;
  }, [
    lastRouteRebuildAt,
    lastSelectedRebuildType,
    resolveAppliedInfo,
    rideDto?.metadata?.routeRebuildAt,
    rideDto?.metadata?.routeRebuildSource,
  ]);

  const persistedRebuildDetailText = useMemo(() => {
    const rebuildRouteType = rideDto?.metadata?.rebuildRouteType;

    return resolveAppliedInfo({
      rebuildRouteType,
      routeRebuildAt: rideDto?.metadata?.routeRebuildAt,
      routeRebuildSource: rideDto?.metadata?.routeRebuildSource,
    })?.detailText;
  }, [
    resolveAppliedInfo,
    rideDto?.metadata?.rebuildRouteType,
    rideDto?.metadata?.routeRebuildAt,
    rideDto?.metadata?.routeRebuildSource,
  ]);

  return {
    sessionRebuildBannerText,
    persistedRebuildDetailText,
  };
};
